import { useState } from "react";
import { MadeWithViny } from "../MadeWithViny";
import { MapPin } from "lucide-react";
import { VerifiedBadge } from "@/components/ui/verified-badge";
import type { TemplateProps, AlbumItem } from "../types";
import { AlbumDetailDrawer } from "../AlbumDetailDrawer";

/* ─────────────────────────────────────────────────────────────
   Índice — tipografia gigante à esquerda, índice tipo "selected
   awards" à direita: título do disco + ano alinhado na borda.
   ───────────────────────────────────────────────────────────── */

function IndexList({
  label,
  items,
  onSelect,
}: {
  label: string;
  items: AlbumItem[];
  onSelect: (a: AlbumItem) => void;
}) {
  if (!items.length) return null;
  const sorted = [...items].sort((a, b) => (a.year ?? 9999) - (b.year ?? 9999));
  return (
    <section className="mt-12 first:mt-0">
      <div className="flex items-baseline justify-between border-b border-neutral-900/10 pb-2">
        <h2 className="font-mono text-[11px] uppercase tracking-[0.18em] text-neutral-900">
          {label}
        </h2>
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-neutral-400">
          Ano
        </span>
      </div>
      <ul className="mt-3">
        {sorted.slice(0, 120).map((a) => (
          <li key={a.id}>
            <button
              type="button"
              onClick={() => onSelect(a)}
              className="group flex w-full items-baseline gap-4 py-[3px] text-left"
            >
              <span className="min-w-0 flex-1 truncate text-[15px] leading-snug text-neutral-600 transition-colors group-hover:text-neutral-950">
                {a.title}
                <span className="text-neutral-400"> · {a.artist}</span>
              </span>
              <span className="shrink-0 font-mono text-[13px] tabular-nums text-neutral-500 transition-colors group-hover:text-neutral-900">
                {a.year ?? "—"}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function IndiceTemplate({ bio, config }: TemplateProps) {
  const [selected, setSelected] = useState<AlbumItem | null>(null);
  const name = config.title || bio.display_name || bio.username;
  const tagline = config.subtitle || bio.bio;
  const collection = config.sections.collection !== false ? bio.owned : [];
  const wishlist = config.sections.wishlist !== false ? bio.wishlist : [];
  const place = [bio.city, bio.country].filter(Boolean).join(", ");

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <div className="mx-auto max-w-[1600px] px-6 py-10 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:gap-16 lg:px-14 lg:py-14">
        {/* IDENTIDADE */}
        <header className="lg:sticky lg:top-14 lg:self-start">
          <div className="flex items-center gap-2 border-l-2 border-neutral-900 pl-2 font-mono text-[11px] uppercase tracking-[0.18em] text-neutral-900">
            {config.eyebrow || "Coleção"}
          </div>

          <h1
            className="mt-4 flex items-center gap-3 font-display font-normal leading-[0.9] tracking-tight"
            style={{ fontSize: "clamp(2.75rem, 6vw, 5.5rem)" }}
          >
            <span className="min-w-0 break-words">{name}</span>
            {bio.is_premium && <VerifiedBadge variant="shimmer" size={26} aria-label="Verificado" />}
          </h1>

          <div className="mt-3 font-mono text-[12px] text-neutral-400">@{bio.username}</div>

          {tagline && (
            <p className="mt-6 max-w-md text-[15px] leading-relaxed text-neutral-600">{tagline}</p>
          )}

          {place && (
            <div className="mt-5 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500">
              <MapPin className="h-3.5 w-3.5" /> {place}
            </div>
          )}

          {bio.favorite_genres.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-1.5">
              {bio.favorite_genres.map((g) => (
                <span
                  key={g}
                  className="rounded-full border border-neutral-900/20 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-neutral-700"
                >
                  {g}
                </span>
              ))}
            </div>
          )}

          <div className="mt-9 flex gap-8">
            <div>
              <div className="font-display text-2xl font-normal tabular-nums">
                {collection.length}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">
                Coleção
              </div>
            </div>
            <div>
              <div className="font-display text-2xl font-normal tabular-nums">
                {wishlist.length}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">
                Interesse
              </div>
            </div>
          </div>

          <div className="mt-10 hidden lg:block">
            <MadeWithViny />
          </div>
        </header>

        {/* ÍNDICE */}
        <main className="mt-14 lg:mt-0">
          {collection.length + wishlist.length === 0 ? (
            <div className="grid place-items-center border border-dashed border-neutral-300 py-28 text-sm text-neutral-500">
              A coleção aparecerá aqui.
            </div>
          ) : (
            <>
              <IndexList label="Coleção selecionada" items={collection} onSelect={setSelected} />
              <IndexList label="Lista de interesse" items={wishlist} onSelect={setSelected} />
            </>
          )}
        </main>
      </div>

      <footer className="flex items-center justify-between border-t border-neutral-900/10 px-6 py-5 font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-500 lg:hidden">
        <MadeWithViny />
        <span>{collection.length + wishlist.length} itens</span>
      </footer>

      <AlbumDetailDrawer
        album={selected}
        open={!!selected}
        onOpenChange={(v) => !v && setSelected(null)}
      />
    </div>
  );
}
